/**
 * factionThemes.js — canonical color / theme table for the four factions.
 *
 * SOURCE OF TRUTH for every faction-tied color currently used anywhere in
 * the app. The diagnostic that led to this file found the SAME faction
 * carried DIFFERENT hex values across five different files (App.css
 * :root/[data-faction], Pages.css Home rings + Puzzle zone accents,
 * FactionWars.jsx FACTION_COLORS, Games.jsx POSITIONS.color). Each of
 * those variants is preserved below as a NAMED field — no color has been
 * merged, normalized, or "picked" as canonical. Consolidation into ONE
 * "correct" per-faction hex is a follow-up decision, not this file's job.
 *
 * Consumers today:
 *   • App.css                — [data-faction="…"] {} blocks mirror
 *                              tokenColors + themeGlow.
 *   • pages/Pages.css        — Home ring/frame colors + .zone-faction-*
 *                              accents mirror homeRing / puzzleZone.
 *   • pages/FactionWars.jsx  — FACTION_COLORS[position] reads
 *                              factionWarsSeatColor from this file.
 *   • pages/Games.jsx        — POSITIONS[i].color reads
 *                              gamesLobbySeatColor from this file.
 *
 * When you eventually pick one canonical hex per faction, edit the value
 * here first and then update the CSS mirrors to match — this file
 * remains the single JS-side source-of-truth from which everything else
 * is either imported directly or documented to mirror.
 */

// Seat position → faction value. Used by seat-keyed consumers
// (FactionWars.jsx, Games.jsx) so the mapping lives in one place.
export const SEAT_TO_FACTION = {
  south: "iron-kingdom",
  west:  "dragonborn",
  north: "elf-court",
  east:  "celestial-host",
};

export const FACTION_THEMES = {
  "iron-kingdom": {
    // Canonical pair for this faction.
    primary:   "#A6802A",
    highlight: "#C9A74B",
    // Every field below is now DERIVED from the pair above:
    //   • *primary*   → gilt / ringColor / frameColor / puzzleZone.accent
    //   • *highlight* → giltLight / wordmarkFill / ctaGlow / seat colors
    //   • rgba glows  → highlight at a fixed opacity (55% themeGlow, 45% zoneGlow)
    tokenColors: {
      gilt:      "#A6802A",
      giltLight: "#C9A74B",
      royal:     "#7A1E1E",
      royalDeep: "#5A1212",
      themeGlow: "rgba(201, 167, 75, 0.55)",  // highlight @ 55%
    },
    homeRing: {
      ringColor:    "#A6802A",
      frameColor:   "#A6802A",
      ctaGlow:      "#C9A74B",
      wordmarkFill: "#C9A74B",
    },
    puzzleZone: {
      accent:     "#A6802A",
      accentGlow: "rgba(201, 167, 75, 0.45)",  // highlight @ 45%
    },
    factionWarsSeatColor: "#C9A74B",
    gamesLobbySeatColor:  "#C9A74B",
  },

  "dragonborn": {
    primary:   "#8C2424",
    highlight: "#C4413A",
    tokenColors: {
      gilt:      "#8C2424",
      giltLight: "#C4413A",
      royal:     "#5E0F0F",
      royalDeep: "#3F0707",
      themeGlow: "rgba(196, 65, 58, 0.55)",   // highlight @ 55%
    },
    homeRing: {
      ringColor:    "#8C2424",
      frameColor:   "#8C2424",
      ctaGlow:      "#C4413A",
      wordmarkFill: "#C4413A",
    },
    puzzleZone: {
      accent:     "#8C2424",
      accentGlow: "rgba(196, 65, 58, 0.45)",   // highlight @ 45%
    },
    factionWarsSeatColor: "#C4413A",
    gamesLobbySeatColor:  "#C4413A",
  },

  "elf-court": {
    primary:   "#2F6B3A",
    highlight: "#4FA058",
    tokenColors: {
      gilt:      "#2F6B3A",
      giltLight: "#4FA058",
      royal:     "#284A24",
      royalDeep: "#1A3318",
      themeGlow: "rgba(79, 160, 88, 0.55)",    // highlight @ 55%
    },
    homeRing: {
      ringColor:    "#2F6B3A",
      frameColor:   "#2F6B3A",
      ctaGlow:      "#4FA058",
      wordmarkFill: "#4FA058",
    },
    puzzleZone: {
      accent:     "#2F6B3A",
      accentGlow: "rgba(79, 160, 88, 0.45)",    // highlight @ 45%
    },
    factionWarsSeatColor: "#4FA058",
    gamesLobbySeatColor:  "#4FA058",
  },

  "celestial-host": {
    primary:   "#2A4E8C",
    highlight: "#4B7BC9",
    tokenColors: {
      gilt:      "#2A4E8C",
      giltLight: "#4B7BC9",
      royal:     "#1E3A6E",
      royalDeep: "#122655",
      themeGlow: "rgba(75, 123, 201, 0.55)",   // highlight @ 55%
    },
    homeRing: {
      ringColor:    "#2A4E8C",
      frameColor:   "#2A4E8C",
      ctaGlow:      "#4B7BC9",
      wordmarkFill: "#4B7BC9",
    },
    puzzleZone: {
      accent:     "#2A4E8C",
      accentGlow: "rgba(75, 123, 201, 0.45)",   // highlight @ 45%
    },
    factionWarsSeatColor: "#4B7BC9",
    gamesLobbySeatColor:  "#4B7BC9",
  },
};

// The Master-tier "Dragon's Eye" puzzle zone is not a real player faction
// but shares the same .zone-faction-* CSS shape. Preserved here for
// completeness so Puzzles.jsx / Pages.css have one lookup surface.
// Pages.css L1338.
export const PUZZLE_MASTER_ZONE = {
  accent:     "#9447B5",
  accentGlow: "rgba(176, 91, 209, 0.5)",
};

// Convenience: seat-keyed color maps (position → hex) derived from
// FACTION_THEMES so consumers that key by position don't need to know
// the position→faction mapping themselves.
export const FACTION_WARS_SEAT_COLORS = Object.fromEntries(
  Object.entries(SEAT_TO_FACTION).map(([seat, faction]) => [
    seat,
    FACTION_THEMES[faction].factionWarsSeatColor,
  ])
);

export const GAMES_LOBBY_SEAT_COLORS = Object.fromEntries(
  Object.entries(SEAT_TO_FACTION).map(([seat, faction]) => [
    seat,
    FACTION_THEMES[faction].gamesLobbySeatColor,
  ])
);
